import React, { useEffect, useState } from 'react';
import { CloudIcon, PlusIcon, ShieldCheckIcon, Trash2Icon, UserPlusIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { addTeamMember, getTeamMembers, loadTeamMembers, removeTeamMember, type TeamMember, type TeamRole } from '../../data/teamAccess';
export function TeamAccessPanel() {
  const [members, setMembers] = useState<TeamMember[]>(() => getTeamMembers());
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState<TeamRole>('manager');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const refresh = async () => {
    const {
      members: next,
      error: failure
    } = await loadTeamMembers();
    setMembers(next);
    setError(failure ?? '');
  };
  useEffect(() => {
    void refresh();
  }, []);
  const add = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!name.trim() || !email.trim()) {
      setError('Add a name and an email address — sign-in links are sent by email.');
      return;
    }
    setBusy(true);
    const failure = await addTeamMember({
      name,
      email,
      phone,
      role
    });
    setBusy(false);
    if (failure) {
      setError(failure);
      return;
    }
    setName('');
    setEmail('');
    setPhone('');
    setRole('manager');
    await refresh();
  };
  const remove = async (member: TeamMember) => {
    setBusy(true);
    const failure = await removeTeamMember(member.email);
    setBusy(false);
    if (failure) {
      setError(failure);
      return;
    }
    await refresh();
  };
  return <section aria-labelledby="team-access" className="rounded-2xl border border-white/[0.07] bg-ink-850 p-5" data-id="element-635" data-mp="grf7m9vqp138">
      <div className="mb-4" data-id="element-636" data-mp="cpk5j35zijoe">
        <h3 id="team-access" className="flex items-center gap-2 text-[15px] font-semibold text-white" data-id="element-637" data-mp="cos9xsce0ly4">
          <ShieldCheckIcon className="h-4 w-4 text-accent-soft" data-id="element-638" data-mp="7s1vf9g8dyet" /> Who can log in
        </h3>
        <p className="mt-1 max-w-2xl text-[12.5px] leading-relaxed text-zinc-500" data-id="element-639" data-mp="3u7fwssjp7fnk">
          The approved list, shared across every device. Only these email addresses can open the studio — anything else
          is rejected at the login screen. Signing in emails a one-tap link, so there is no shared password to leak.
        </p>
        <p className="mt-1.5 flex items-center gap-1.5 text-[11px] text-zinc-600" data-id="element-640" data-mp="3p9o5o0qvnxkf">
          <CloudIcon className="h-3.5 w-3.5" data-id="element-641" data-mp="gu4hhg52d5c8" /> Stored in your database, so adding someone here lets them in from any
          device immediately.
        </p>
      </div>

      <ul className="mb-4 divide-y divide-white/[0.06]" data-id="element-642" data-mp="74kxg75m75fs">
        {members.map(member => <li key={member.id} className="flex items-center justify-between gap-3 py-2.5 first:pt-0" data-id="element-643" data-mp="c24cvae1to9b">
            <div className="min-w-0" data-id="element-644" data-mp="zs709d70q61l">
              <div className="flex items-center gap-2" data-id="element-645" data-mp="zr5rjfdh5vn1">
                <span className="truncate text-[13.5px] font-medium text-white" data-id="element-646" data-mp="14jgoqagtz3bw">{member.name}</span>
                <span className="shrink-0 rounded-full border border-white/[0.1] px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-zinc-500" data-id="element-647" data-mp="2p8sv7m2dcgo5">
                  {member.role}
                </span>
              </div>
              <div className="truncate text-[11.5px] text-zinc-500" data-id="element-648" data-mp="2y0mg1koxkdjj">
                {[member.email, member.phone].filter(Boolean).join(' · ')}
              </div>
            </div>
            {member.role !== 'owner' && <button type="button" onClick={() => void remove(member)} disabled={busy} aria-label={`Remove ${member.name}`} className="shrink-0 rounded-lg p-1.5 text-zinc-500 transition-colors duration-150 ease-out hover:bg-danger/10 hover:text-danger disabled:opacity-40" data-id="element-649" data-mp="26cucfvv6zuts">
                <Trash2Icon className="h-4 w-4" data-id="element-650" data-mp="3qpa4fp4z7j1b" />
              </button>}
          </li>)}
      </ul>

      <form onSubmit={add} className="rounded-xl border border-dashed border-white/[0.12] bg-ink-800 p-3" data-id="element-651" data-mp="3lr8o3byxpxtj">
        <div className="mb-2 flex items-center gap-1.5 text-[12px] font-medium text-zinc-300" data-id="element-652" data-mp="kydmqqwd9lg1">
          <UserPlusIcon className="h-3.5 w-3.5 text-accent-soft" data-id="element-653" data-mp="pxgfpcz2o17n" /> Add someone
        </div>
        <div className="grid gap-2 sm:grid-cols-2" data-id="element-654" data-mp="ut5hlmykdu74">
          <SmallField label="Name" value={name} onChange={setName} placeholder="Jordan Reyes" data-id="element-655" data-mp="zu31c3z82waq" />
          <SmallField label="Email" value={email} onChange={setEmail} placeholder="jordan@example.com" data-id="element-656" data-mp="13volmae36cuy" />
          <SmallField label="Mobile number (optional)" value={phone} onChange={setPhone} placeholder="+1 512 555 0114" data-id="element-657" data-mp="18tgaswncjkvg" />
          <label className="block" data-id="element-658" data-mp="1dm9gbmqn2x8l">
            <span className="mb-1 block text-[11px] font-medium text-zinc-400" data-id="element-659" data-mp="1dnardr7nnjqb">Role</span>
            <select value={role} onChange={event => setRole(event.target.value as TeamRole)} className="w-full rounded-lg border border-white/[0.1] bg-ink-850 px-2.5 py-2 text-[12.5px] text-white outline-none focus:border-[#d97742]/70" data-id="element-660" data-mp="1im461myohgs2">
              <option value="manager" data-id="element-661" data-mp="3c436u5x4fulj">Manager · full studio access</option>
              <option value="owner" data-id="element-662" data-mp="380djtkw3hl2m">Owner · cannot be removed</option>
            </select>
          </label>
        </div>

        {error && <p role="alert" className="mt-2 text-[11.5px] leading-relaxed text-rose-300" data-id="element-663" data-mp="333wu4m98yynf">
            {error}
          </p>}

        <div className="mt-3" data-id="element-664" data-mp="3vr0taalnsira">
          <Button variant="secondary" size="sm" type="submit" disabled={busy} data-id="element-665" data-mp="3vq7vhz4oh3la">
            <PlusIcon className="h-3.5 w-3.5" data-id="element-666" data-mp="3qspl04b0igb6" /> {busy ? 'Saving…' : 'Add to access list'}
          </Button>
        </div>
      </form>

      <p className="mt-3 text-[11px] leading-relaxed text-zinc-600" data-id="element-667" data-mp="3lz3dsrnmb4mr">
        Everyone on this list edits the same shared site content from any device. Mobile numbers are kept for your own
        reference — links are sent by email. The “Continue without signing in” shortcut only appears while building and
        never on the published site.
      </p>
    </section>;
}
function SmallField({
  label,
  value,
  onChange,
  placeholder
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return <label className="block" data-id="element-668" data-mp="1y3yk32nj23qk">
      <span className="mb-1 block text-[11px] font-medium text-zinc-400" data-id="element-669" data-mp="1y2wyw5css0v4">{label}</span>
      <input value={value} onChange={event => onChange(event.target.value)} placeholder={placeholder} className="w-full rounded-lg border border-white/[0.1] bg-ink-850 px-2.5 py-2 text-[12.5px] text-white outline-none placeholder:text-zinc-600 focus:border-[#d97742]/70" data-id="element-670" data-mp="230yrbgw45cj9" />
    </label>;
}